import React from 'react'
import { T } from '@transifex/react'

import { pathJoin } from 'src/reducers/routing/utils'

import { createAssetManagementRoutesMap } from './asset-management'
import { createSubscriptionRoutesMap } from './billing'
import { createCRMRoutesMap } from './crm'
import { createCrmPortalViewRoutesMap } from './crm-portal'
import { createCustomFieldsRoutesMap } from './custom-fields'
import { createFleetManagementRoutesMap } from './fleet-management'
import { createKmdEasyRoutesMap } from './kmd-easy'
import { createMapLayersZonesRoutesMap } from './map-layers-zones'
import { createNavigationAppRoutesMap } from './navigation-app'
import { createOperationManagementRoutesMap } from './operation-management'
import { createProductManagementRoutesMap } from './product-management'
import { createTicketsRoutesMap } from './tickets'
import { createUserMangementRoutesMap } from './user-management'
import { createVerdisRoutesMap } from './verdis'

export const createCompanyRoutesMap = (prefix: string) => ({
  general: pathJoin(prefix)('general').chainWithTitle({
    title: <T _str="General" />,
    breadCrumbTitle: <T _str="General" />,
  }),
  'company-settings': pathJoin(prefix)('company-settings').chainWithTitle({
    title: <T _str="Company settings" />,
    breadCrumbTitle: <T _str="Company settings" />,
  }),
  // Legacy path kept only so the old `/settings/company/:id/settings` URL
  // redirects to `.../company-settings` (see route-config). No card links here.
  settings: pathJoin(prefix)('settings').chainWithTitle({
    title: <T _str="Company settings" />,
    breadCrumbTitle: <T _str="Company settings" />,
  }),
  // Renamed "Contract Management" → "Products & prices": the actual
  // contracts — contract areas with award, validity, termination and
  // conflict checking — live in Operations / Settings → Hauler, so
  // "contract" on this module pointed municipal users at the wrong door
  // (the same trap NEXUS-608's "Contact" vs "Individual" documents). The
  // route KEY stays `product-management`: display only, no URL change.
  'product-management': pathJoin(prefix)('product-management').chainMapAuto(
    createProductManagementRoutesMap,
    {
      title: <T _str="Products & prices" />,
      breadCrumbTitle: <T _str="Products & prices" />,
    }
  ),
  'crm-portal': pathJoin(prefix)('crm-portal').chainMapAuto(
    createCrmPortalViewRoutesMap,
    {
      title: <T _str="Portal Settings" />,
      breadCrumbTitle: <T _str="Portal Settings" />,
    }
  ),
  'email-accounts': pathJoin(prefix)('email-accounts').chainWithTitle({
    title: <T _str="Email Accounts" />,
    breadCrumbTitle: <T _str="Email Accounts" />,
  }),
  'criipto-application': pathJoin(prefix)('criipto-application').chainWithTitle(
    {
      title: <T _str="Criipto Application" />,
      breadCrumbTitle: <T _str="Criipto Application" />,
    }
  ),
  'digital-post': pathJoin(prefix)('digital-post').chainWithTitle({
    title: <T _str="Digital Post" />,
    breadCrumbTitle: <T _str="Digital Post" />,
  }),
  verdis: pathJoin(prefix)('verdis').chainMapAuto(createVerdisRoutesMap, {
    title: <T _str="Verdis" />,
    breadCrumbTitle: <T _str="Verdis" />,
  }),
  'kmd-easy': pathJoin(prefix)('kmd-easy').chainMapAuto(
    createKmdEasyRoutesMap,
    {
      title: <T _str="KMD Easy" />,
      breadCrumbTitle: <T _str="KMD Easy" />,
    }
  ),
  enhetsregisteret: pathJoin(prefix)('enhetsregisteret').chainWithTitle({
    title: <T _str="Enhetsregisteret" />,
    breadCrumbTitle: <T _str="Enhetsregisteret" />,
  }),
  crm: pathJoin(prefix)('crm').chainMapAuto(createCRMRoutesMap, {
    title: <T _str="Customer Management" />,
    breadCrumbTitle: <T _str="Customer Management" />,
  }),
  'fleet-management': pathJoin(prefix)('fleet-management').chainMapAuto(
    createFleetManagementRoutesMap,
    {
      title: <T _str="Fleet Management" />,
      breadCrumbTitle: <T _str="Fleet Management" />,
    }
  ),
  billing: pathJoin(prefix)('billing').chainMapAuto(
    createSubscriptionRoutesMap,
    {
      title: <T _str="Billing" />,
      breadCrumbTitle: <T _str="Billing" />,
    }
  ),
  tickets: pathJoin(prefix)('tickets').chainMapAuto(createTicketsRoutesMap, {
    title: <T _str="Tickets" />,
    breadCrumbTitle: <T _str="Tickets" />,
  }),
  'user-management': pathJoin(prefix)('user-management').chainMapAuto(
    createUserMangementRoutesMap,
    {
      title: <T _str="User Management" />,
      breadCrumbTitle: <T _str="User Management" />,
    }
  ),
  'operation-management': pathJoin(prefix)('operation-management').chainMapAuto(
    createOperationManagementRoutesMap,
    {
      title: <T _str="Operation Management" />,
      breadCrumbTitle: <T _str="Operation Management" />,
    }
  ),
  // Driver-GPS compliance panel (live-ops #19): the monitoring register,
  // retention, access audit and DPIA evidence. Behind the
  // live_operations__compliance_admin permission (route + menu gating in the
  // company-setting route-config / menu-list).
  'gps-compliance': pathJoin(prefix)('gps-compliance').chainWithTitle({
    title: <T _str="Driver GPS & privacy" />,
    breadCrumbTitle: <T _str="Driver GPS & privacy" />,
  }),
  // ADS/EPR waste-reporting page (live-ops ADS bridge): per-fraction
  // collected weights mapped to EAK codes for one month, the
  // submission-readiness checklist, and the CSV/XLSX download for
  // Affaldsdatasystemet. Office-only server-side; the FE gate (live-ops
  // flag + view permission) is cosmetic.
  'ads-report': pathJoin(prefix)('ads-report').chainWithTitle({
    title: <T _str="Waste reporting (ADS)" />,
    breadCrumbTitle: <T _str="Waste reporting (ADS)" />,
  }),
  // PRODUCT-492 (skiphire): Settings → Skip hire setup — the stepper over
  // BE-H3's idempotent setup service (flags, yards, skip types and labels,
  // products, ticket types and date rules, skip route, truck). Staff or the
  // company master run it (route + menu gating in route-config / menu-list).
  'skip-hire-setup': pathJoin(prefix)('skip-hire-setup').chainWithTitle({
    title: <T _str="Skip hire setup" />,
    breadCrumbTitle: <T _str="Skip hire setup" />,
  }),
  // PRODUCT-489 (skiphire): Settings → Council fee table — the authorities
  // that grant public-road skip permits, their fee product, standard
  // duration and lead time (the rental form's permit fields read it).
  'council-fee-table': pathJoin(prefix)('council-fee-table').chainWithTitle({
    title: <T _str="Council fee table" />,
    breadCrumbTitle: <T _str="Council fee table" />,
  }),
  // PRODUCT-503 (skiphire): Settings → Waste fraction rules — which waste
  // fractions a container type may NOT take (the booking hard stop of
  // BE-P2-5); an Inventory-module capability like the council fee table.
  'waste-fraction-rules': pathJoin(prefix)(
    'waste-fraction-rules'
  ).chainWithTitle({
    title: <T _str="Waste fraction rules" />,
    breadCrumbTitle: <T _str="Waste fraction rules" />,
  }),
  'alert-rules': pathJoin(prefix)('alert-rules').chainMapAuto(
    (prefix2) => ({
      ':id': (id = ':id') => pathJoin(prefix2)(id).chain,
    }),
    {
      title: <T _str="Alert Rules" />,
      breadCrumbTitle: <T _str="Alert Rules" />,
    }
  ),
  documents: pathJoin(prefix)('documents').chainWithTitle({
    title: <T _str="Documents" />,
    breadCrumbTitle: <T _str="Documents" />,
  }),
  // fixes-round C4 (D24): Settings → Invoicing — minimum invoice amounts +
  // the default invoice-row bundling strategy (invoicing-enabled tenants).
  // Onboarding B2: it also hosts the seller identity now, which is what the
  // backend's refuse-to-issue message points at by name — so the crumb is
  // "Invoicing", the words that message uses, not "Invoicing defaults".
  invoicing: pathJoin(prefix)('invoicing').chainWithTitle({
    title: <T _str="Invoicing" />,
    breadCrumbTitle: <T _str="Invoicing" />,
  }),
  communication: pathJoin(prefix)('communication').chainMapAuto(
    (prefix2) => ({
      ':id': (id = ':id') => pathJoin(prefix2)(id).chain,
    }),
    {
      title: <T _str="Communication" />,
      breadCrumbTitle: <T _str="Communication" />,
    }
  ),
  'asset-management': pathJoin(prefix)('asset-management').chainMapAuto(
    createAssetManagementRoutesMap,
    {
      title: <T _str="Asset Management" />,
      breadCrumbTitle: <T _str="Asset Management" />,
    }
  ),
  // DEV TASK 4: platform-level zones + map layers (zones moved out of the
  // hauler world — every module is a consumer). PRO-400: now a routed-tab
  // section (Zones / Map layers / Municipalities).
  'map-layers-zones': pathJoin(prefix)('map-layers-zones').chainMapAuto(
    createMapLayersZonesRoutesMap,
    {
      title: <T _str="Map layers & zones" />,
      breadCrumbTitle: <T _str="Map layers & zones" />,
    }
  ),
  history: pathJoin(prefix)('history').chainWithTitle({
    title: <T _str="History" />,
    breadCrumbTitle: <T _str="History" />,
  }),
  'navigation-app': pathJoin(prefix)('navigation-app').chainMapAuto(
    createNavigationAppRoutesMap,
    {
      title: <T _str="Navigation App" />,
      breadCrumbTitle: <T _str="Navigation App" />,
    }
  ),
  'custom-fields': pathJoin(prefix)('custom-fields').chainMapAuto(
    createCustomFieldsRoutesMap,
    {
      title: <T _str="Custom Fields" />,
      breadCrumbTitle: <T _str="Custom Fields" />,
    }
  ),
  matrikkel: pathJoin(prefix)('matrikkel').chainWithTitle({
    title: <T _str="Matrikkel" />,
    breadCrumbTitle: <T _str="Matrikkel" />,
  }),
  visma: pathJoin(prefix)('visma').chainMapAuto(
    (prefix2) => ({
      // `…/visma/runs/<logId>/dropped`: the raw, copyable list of agreements
      // one export run skipped. Opened in a new tab from the run summary; the
      // run's own row stays in the export log, so no `runs/:logId` page exists.
      'runs/:logId': (logId = ':logId') =>
        pathJoin(prefix2)('runs')(logId).chainMapAuto((prefix3) => ({
          dropped: pathJoin(prefix3)('dropped').chainWithTitle({
            title: <T _str="Skipped agreements" />,
            breadCrumbTitle: <T _str="Skipped agreements" />,
          }),
        })),
    }),
    {
      title: <T _str="Visma" />,
      breadCrumbTitle: <T _str="Visma" />,
    }
  ),
  ryhti: pathJoin(prefix)('ryhti').chainWithTitle({
    title: <T _str="RYHTI" />,
    breadCrumbTitle: <T _str="RYHTI" />,
  }),
})
